// Copied from apps/admin/src/features/gps-stops/model/stop-types.ts (PRD #875).
import { Namespace, type KeysOf } from "@racoon/i18n";

// gps_stop meta.stop_type, written by vrp_solver's extract_gps_stops (vrp_solver#175).
// PICKUP_LOCATION_PLANNED: a site on that day's route; PICKUP_LOCATION: a site off it;
// UNKNOWN matched nothing; NO_NEARBY_PL is an UNKNOWN dwell of 10+ minutes.

export const GPS_STOP_TYPES = [
  "PICKUP_LOCATION_PLANNED",
  "PICKUP_LOCATION",
  "DISPOSAL",
  "DEPOT",
  "SUSPICIOUS",
  "UNKNOWN",
  "NO_NEARBY_PL",
  "UNCLASSIFIED",
] as const;

export type GpsStopType = (typeof GPS_STOP_TYPES)[number];

export type GpsStopLabelKey = KeysOf<Namespace.GpsTracking>;

export type GpsStopMarkerShape = "circle" | "diamond" | "square" | "triangle" | "hollow";

export interface GpsStopTypeConfig {
  /** gps-tracking translation key for the marker tooltip. */
  labelKey: GpsStopLabelKey;
  color: string;
  shape: GpsStopMarkerShape;
}

export const GPS_STOP_TYPE_CONFIG: Record<GpsStopType, GpsStopTypeConfig> = {
  PICKUP_LOCATION_PLANNED: {
    labelKey: "gpsStopAtSite",
    // Neutral, not green: a green dot passed for a completed job's pin.
    color: "#334155",
    shape: "circle",
  },
  PICKUP_LOCATION: {
    labelKey: "gpsStopAtSiteOffPlan",
    color: "#EAB308",
    shape: "circle",
  },
  DISPOSAL: {
    labelKey: "gpsStopUnloading",
    color: "#2563EB",
    shape: "diamond",
  },
  DEPOT: {
    labelKey: "gpsStopDepot",
    color: "#7C3AED",
    shape: "square",
  },
  SUSPICIOUS: {
    labelKey: "gpsStopIllegal",
    color: "#DC2626",
    shape: "triangle",
  },
  UNKNOWN: {
    labelKey: "gpsStopSuspicious",
    color: "#EA580C",
    shape: "hollow",
  },
  NO_NEARBY_PL: {
    labelKey: "gpsStopSuspicious",
    color: "#EA580C",
    shape: "hollow",
  },
  UNCLASSIFIED: {
    labelKey: "gpsStopUnclassified",
    color: "#A8A29E",
    shape: "hollow",
  },
};

export interface GpsStopLegendGroup {
  key: string;
  labelKey: GpsStopLabelKey;
  /** Swatch, matching the marker every type in the group draws. */
  color: string;
  shape: GpsStopMarkerShape;
  types: readonly GpsStopType[];
  /** Listed even at a count of 0, so an empty result is still a statement. */
  alwaysShown?: boolean;
}

/**
 * Legend rows. A group's types toggle together, so their marker styling has to agree.
 */
export const GPS_STOP_LEGEND_GROUPS: readonly GpsStopLegendGroup[] = [
  {
    key: "PICKUP_LOCATION_PLANNED",
    labelKey: "gpsStopAtSite",
    color: GPS_STOP_TYPE_CONFIG.PICKUP_LOCATION_PLANNED.color,
    shape: "circle",
    types: ["PICKUP_LOCATION_PLANNED"],
  },
  {
    key: "PICKUP_LOCATION",
    labelKey: "gpsStopAtSiteOffPlan",
    color: GPS_STOP_TYPE_CONFIG.PICKUP_LOCATION.color,
    shape: "circle",
    types: ["PICKUP_LOCATION"],
  },
  {
    key: "DISPOSAL",
    labelKey: "gpsStopUnloading",
    color: GPS_STOP_TYPE_CONFIG.DISPOSAL.color,
    shape: "diamond",
    types: ["DISPOSAL"],
  },
  {
    key: "DEPOT",
    labelKey: "gpsStopDepot",
    color: GPS_STOP_TYPE_CONFIG.DEPOT.color,
    shape: "square",
    types: ["DEPOT"],
  },
  {
    key: "SUSPICIOUS",
    labelKey: "gpsStopIllegal",
    color: GPS_STOP_TYPE_CONFIG.SUSPICIOUS.color,
    shape: "triangle",
    types: ["SUSPICIOUS"],
  },
  {
    key: "UNMATCHED",
    labelKey: "gpsStopSuspiciousPlural",
    color: GPS_STOP_TYPE_CONFIG.UNKNOWN.color,
    shape: "hollow",
    types: ["UNKNOWN", "NO_NEARBY_PL"],
    alwaysShown: true,
  },
  {
    key: "UNCLASSIFIED",
    labelKey: "gpsStopUnclassified",
    color: GPS_STOP_TYPE_CONFIG.UNCLASSIFIED.color,
    shape: "hollow",
    types: ["UNCLASSIFIED"],
  },
];

/**
 * Above this, job_stop_detector's own recorded average contradicts its claim
 * that the truck stopped. One notch below the 10.0 km/h cap extract_gps_stops
 * enforces; measured against PICKUP_LOCATION_PLANNED, 9 cuts 33.1% for 6.2%.
 */
export const MAX_STOP_SPEED_KMPH = 9;

/**
 * Model output no other source corroborates. PICKUP_LOCATION_PLANNED is out
 * because the route plan backs it, and filtering it loses real service events.
 */
export const UNCORROBORATED_GPS_STOP_TYPES: readonly GpsStopType[] = [
  "PICKUP_LOCATION",
  "NO_NEARBY_PL",
];

/**
 * Off-site dwells shorter than this are hidden: most of them are traffic stops on a city
 * street. A stopgap until the API filters them itself.
 */
export const MIN_OFF_SITE_STOP_SECONDS = 180;

/** Types held to MIN_OFF_SITE_STOP_SECONDS: the off-plan site and the unmatched stops. */
export const OFF_SITE_GPS_STOP_TYPES: readonly GpsStopType[] = [
  "PICKUP_LOCATION",
  "UNKNOWN",
  "NO_NEARBY_PL",
];

export function isKnownGpsStopType(value: string): value is GpsStopType {
  return (GPS_STOP_TYPES as readonly string[]).includes(value);
}

/** Types only a run that matched against pickup locations can produce. */
export const PICKUP_LOCATION_AWARE_TYPES: readonly GpsStopType[] = [
  "PICKUP_LOCATION",
  "PICKUP_LOCATION_PLANNED",
  "NO_NEARBY_PL",
];
